import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { CONTINGENCY_CATEGORY, sortPlans, shipDate } from '../lib/contingency'
import { drillLinks, drillsReport, lastDrilledLabel } from '../lib/drillPlans'
import { getShipTz } from '../lib/shipTime'

// Drills <-> Contingency Plans on the drills page (Drill Matrix > Plans): which plan each drill type runs in drill mode (a vessel
// setting: Checklists Edit changes it, View sees it), and the drills report - every plan by its last drill, never drilled first.
// useDrillPlans reads the three things the drill pages need: the vessel's plans, the links, the drill runs (when each plan was drilled).
export function useDrillPlans(clientId) {
  const [data, setData] = useState({ loading: true, plans: [], links: [], runs: [], linksMissing: false, runsMissing: false })
  const load = useCallback(async () => {
    if (!clientId) return
    const [p, l, r] = await Promise.all([
      supabase.from('checklists').select('id,title,category,library_id').eq('client_id', clientId).eq('category', CONTINGENCY_CATEGORY),
      supabase.from('drill_plan_links').select('id,drill_type,checklist_id,updated_by,updated_at').eq('client_id', clientId),
      supabase.from('checklist_instances').select('checklist_id,completed_at').eq('client_id', clientId).eq('drill', true).limit(5000),
    ])
    setData({ loading: false, plans: sortPlans(p.data || []), links: l.error ? [] : (l.data || []), runs: r.error ? [] : (r.data || []), linksMissing: !!l.error, runsMissing: !!r.error })
  }, [clientId])
  useEffect(() => { load() }, [load])
  return { ...data, reload: load }
}

const th = { fontSize: 11.5, fontWeight: 700, color: 'var(--muted)', textAlign: 'left', padding: '9px 12px', textTransform: 'uppercase', letterSpacing: '.04em' }
const td = { fontSize: 13.5, color: 'var(--text)', padding: '9px 12px', verticalAlign: 'middle' }
const btn = { minHeight: 36, padding: '0 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }
const SOURCE = { set: 'set for this vessel', default: 'default (by title)', none: 'no plan', unlinked: 'not linked' }

export default function DrillPlansPanel({ clientId, data, canEdit, who, onRunPlan }) {
  const [err, setErr] = useState('')
  const tz = getShipTz()
  const rows = drillLinks(data.links, data.plans)
  const report = drillsReport(data.plans, data.runs)
  async function setLink(drillType, value) {
    setErr('')
    // '' = back to the default by title (the row goes); 'none' = no plan on purpose; else the plan's id
    const res = value === ''
      ? await supabase.from('drill_plan_links').delete().eq('client_id', clientId).eq('drill_type', drillType)
      : await supabase.from('drill_plan_links').upsert({ client_id: clientId, drill_type: drillType, checklist_id: value === 'none' ? null : value, updated_by: who || null, updated_at: new Date().toISOString() }, { onConflict: 'client_id,drill_type' })
    if (res.error) setErr('Not saved: ' + res.error.message + (/drill_plan_links|schema cache|does not exist/i.test(res.error.message) ? ' (sql/drills-contingency.sql is not run yet)' : ''))
    data.reload()
  }
  return (
    <div>
      <h3 style={{ fontSize: 15, margin: '0 0 6px' }}>Drill type {'→'} Contingency Plan</h3>
      <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: '0 0 10px' }}>"Run drill" opens the linked plan in drill mode (same steps and timeline, attendance, debrief); finishing it records the drill and moves the next due date. Fire, Abandon Ship, MOB, Enclosed Space and Medical link by title unless set here.{canEdit ? '' : ' Changing a link needs Checklists Edit.'}</p>
      {data.linksMissing && <p style={{ fontSize: 12.5, color: '#8a5c0a' }}>The links are not stored yet (sql/drills-contingency.sql is not run): the defaults by title apply.</p>}
      {err && <p role="alert" style={{ color: '#C62828', fontSize: 13 }}>{err}</p>}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
          <thead><tr><th style={th}>Drill</th><th style={th}>Plan run in drill mode</th><th style={th}>Link</th></tr></thead>
          <tbody>{rows.map(r => {
            const cur = data.links.find(l => l.drill_type === r.drill.label)
            const val = !cur ? '' : cur.checklist_id ? cur.checklist_id : 'none'
            return (
              <tr key={r.drill.key} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={td}>{r.drill.label}</td>
                <td style={td}>{canEdit && !data.linksMissing
                  ? <select aria-label={'Plan for the ' + r.drill.label + ' drill'} value={val} onChange={e => setLink(r.drill.label, e.target.value)} style={{ ...btn, fontWeight: 500, maxWidth: 320 }}>
                      <option value="">{r.source === 'default' || (!cur && r.plan) ? 'Default: ' + (r.plan ? r.plan.title : '-') : 'Default (none by title)'}</option>
                      <option value="none">No plan</option>
                      {data.plans.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                    </select>
                  : (r.plan ? r.plan.title : <span style={{ color: 'var(--muted)' }}>-</span>)}</td>
                <td style={{ ...td, color: 'var(--muted)', fontSize: 12.5 }}>{SOURCE[r.source]}</td>
              </tr>
            )
          })}</tbody>
        </table>
      </div>

      <h3 style={{ fontSize: 15, margin: '24px 0 6px' }}>Drills report: Contingency Plans by last drill</h3>
      {data.runsMissing && <p style={{ fontSize: 12.5, color: '#8a5c0a' }}>Drill runs are not recorded yet (sql/drills-contingency.sql is not run).</p>}
      {!report.length ? <p style={{ fontSize: 13, color: 'var(--muted)' }}>No Contingency Plans on this vessel yet (Checklists {'›'} Contingency Plans).</p> : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
            <thead><tr><th style={th}>Plan</th><th style={th}>Last drilled</th><th style={th}>Drills</th><th style={{ ...th, textAlign: 'right' }}></th></tr></thead>
            <tbody>{report.map(x => (
              <tr key={x.plan.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={td}>{x.plan.title}</td>
                <td style={{ ...td, color: x.last ? 'var(--text)' : '#C62828', fontWeight: x.last ? 400 : 700 }}>{x.last ? shipDate(x.last, tz) + ' (' + x.days + ' d ago)' : '⚠ ' + lastDrilledLabel(x)}</td>
                <td style={td}>{x.count}</td>
                <td style={{ ...td, textAlign: 'right' }}>{onRunPlan && <button style={btn} onClick={() => onRunPlan(x.plan)}>Run drill</button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  )
}
